/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { appRoutes } from './app.routes';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  ToolbeltEffects,
  toolBeltFeatureKey,
  toolbeltReducer,
  ApplyPanelDataReducer,
  ApplyPanelDataEffects,
  ApplyPanelDataKey,
  ApplyIdKey,
  ApplyIdReducer,
  ApplyIdEffects,
  AnnotationSettingsEffects,
  SystemManagerEffects,
  AnnotationSettingsKey,
  ImportScheduleKey,
  ImportScheduleReducer,
  ImportScheduleEffects,
  CurateSearchReducer,
  CurateSearchEffects,
  FittingTreeViewEffects,
  FittingTreeViewFacade,
  SystemPanelFrameFacade,
  AppShellFacade,
  AddFittingPopupFacade,
  AddFittingPanelFrameFacade,
  ManageModePanelFrameFacade,
  AnnotationSettingsReducer,
  AnnotationSettingsFacade,
  AddFamilyInformationFacade,
  SystemManagerFacade,
  SystemManagerKey,
  SystemManagerReducer,
  FamilyInformationEffects,
  AddFamilyInformationKey,
  AddFamilyInformationReducer,
  ProjectDataReducer,
  ProjectDataKey,
  ProjectDataEffects,
  SysQueGaugeFacade,
} from '@hcworkspace/sysque/toolbelt/feature';

import { DxTemplateModule } from 'devextreme-angular';
import { AuthGuard } from './auth-guard';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';
import { BackendService, PanelDataService, BuildingDataApiService, CurateSearchService, DataInitializerService, LocalStorageService, SystemStateManager, AnnotationSettingsService, AnnotationSettingsLabelsManager, FittingTreeViewService, ProjectDataService } from '@hcworkspace/sysque/toolbelt/utils';
import { ConfigurationLocationKey, ConfigurationLocationReducer } from 'libs/sysque/toolbelt/feature/src/lib/+state/configuration-location/configuration-location-reducer';
import { ConfigurationLocationEffects } from 'libs/sysque/toolbelt/feature/src/lib/+state/configuration-location/configuration-location-effects';
import { ImportScheduleFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/import-schedule/import-schedule-facade';
import { ConfigurationLocationFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/configuration-location/configuration-location-facade';
import { CONFIGURATION_LOCATION_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/change-configuration-location-facade';
import { IMPORT_SCHEDULE_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/import-schedule-facade';
import { APPLY_ID_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/apply-id-facade-interface';
import { ApplyIdFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/apply-id/apply-id-facade';
import { PanelDataFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/panel-data/panel-data-facade';
import { PANEL_DATA_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/panel-data-facade-interface';
import { RootPathSelectorFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/root-path-selector/root-path-selector-facade';
import { CurateSearchFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/curate-search/curate-search-facade';
import { ADD_FITTING_MODE_FACADE_TOKEN, ADD_FITTING_PANEL_FRAME_FACADE_TOKEN, ADD_FITTING_POPUP_FACADE_TOKEN, ADD_FITTING_TAB_FACADE_TOKEN, APP_SHELL_FACADE_TOKEN, CURATE_SEARCH_FACADE_TOKEN, FITTING_TREE_VIEW_FACADE_TOKEN, MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN, RESULT_CONTAINER_FACADE_TOKEN, SYSTEM_PANEL_FRAME_FACADE_TOKEN, TOOLBELT_FACADE_TOKEN, CABLE_TRAY_SYSTEM_FACADE_TOKEN, PROJECT_DATA_FACADE_TOKEN, SYSQUE_GAUGE_FACADE_TOKEN } from '@hcworkspace/sysque/toolbelt/data-access';
import { AddFittingTabFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/add-fitting-tab/add-fitting-tab-facade';
import { CurateSearchKey } from 'libs/sysque/toolbelt/feature/src/lib/+state/curate-search/curate-search-reducer';
import {
  FittingTreeViewKey,
  FittingTreeViewReducer,
} from 'libs/sysque/toolbelt/feature/src/lib/+state/fitting-tree-view/fitting-tree-view-reducer';
import { ResultContainerFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/result-container/result-container-facade';
import { ToolbeltFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/toolbelt/toolbelt-facade';
import { SelectCategoryFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/select-category/select-category-facade';
import { SelectSystemFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/select-system/select-system-facade';
import { AboutDialogFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/about-dialog/about-dialog-facade';
import { SystemCategoryOptionsFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/sysque-system-category-options/sysque-system-category-options-facade';
import { DeleteSystemCategoryFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/delete-system-category/delete-system-category-facade';
import { CategoryScopeFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/category-scope/category-scope-facade';
import { SysqueTooltipFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/sysque-tooltip/sysque-tooltip-facade';
import { ElectricalSettingsFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/electrical-settings/electrical-settings-facade';
import { ProjectDataFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/project-data/project-data-facade';

import { ROOT_PATH_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/root-path-selector-facade-interface';
import { SELECT_CATEGORY_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/select-category-facade-interface';
import { SELECT_SYSTEM_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/select-system-facade-interface';
import { SYSTEM_CATEGORY_OPTIONS_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/sysque-system-category-options-facade-interface';
import { DELETE_SYSTEM_CATEGORY_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/delete-system-category-facade-interface';
import { ABOUT_DIALOG_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/about-dialog-facade-interface';
import { CATEGORY_SCOPE_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/category-scope-facade-interface';
import { SYSQUE_TOOLTIP_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/sysque-tooltip-facade-interface';
import { ELECTRICAL_SETTINGS_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/electrical-settings-facade-interface';
import {
  ElectricalSettingsKey,
  ElectricalSettingsReducer,
} from 'libs/sysque/toolbelt/feature/src/lib/+state/electrical-settings/electrical-settings-reducer';
import { ElectricalSettingsEffects } from 'libs/sysque/toolbelt/feature/src/lib/+state/electrical-settings/electrical-settings-effects';
import { ANNOTATION_SETTINGS_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/annotation-settings-facade-interface';
import { SYSTEM_MANAGER_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/system-manager-facade-interface';
import { AddFittingModeFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/add-fitting-mode/add-fitting-mode-facade';
import { CableTraySystemFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/cable-tray-system/cable-tray-system-facade';
import { FamilyInformationService } from 'libs/sysque/toolbelt/utils/src/lib/sysque-backend/family-information.service';
import { ADD_FAMILY_INFORMATION_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { IMPORT_SYSTEMS_FACADE_TOKEN } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/import-systems-facade-interface';
import { ImportSystemsFacade } from 'libs/sysque/toolbelt/feature/src/lib/+state/import-systems/import-systems-facade';
import { SysQueStateService } from '../../../../libs/sysque/toolbelt/utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { ColorPickerModule } from 'ngx-color-picker';
import { FITTING_PRIORITIZATION_FACADE_TOKEN } from '../../../../libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/fitting-prioritization-facade-interface';
import { FittingPrioritizationFacade } from '../../../../libs/sysque/toolbelt/feature/src/lib/+state/fitting-prioritization/fitting-prioritization-facade';
import { CONVERT_OPTIONS_FACADE_TOKEN } from '../../../../libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/convert-options-facade-interface';
import { ConvertOptionsFacade } from '../../../../libs/sysque/toolbelt/feature/src/lib/+state/convert-options/convert-options-facade';

@NgModule({
  declarations: [AppComponent],
  imports: [
    DxTemplateModule,
    ColorPickerModule,
    BrowserModule,
    RouterModule.forRoot(appRoutes),
    HttpClientModule,
    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictActionImmutability: false,
          strictStateImmutability: false,
          strictStateSerializability: false,
        },
      }
    ),
    EffectsModule.forRoot([]),
    StoreModule.forFeature(toolBeltFeatureKey, toolbeltReducer),
    StoreModule.forFeature(ApplyPanelDataKey, ApplyPanelDataReducer),
    StoreModule.forFeature(ApplyIdKey, ApplyIdReducer),
    StoreModule.forFeature(ImportScheduleKey, ImportScheduleReducer),
    StoreModule.forFeature(ConfigurationLocationKey, ConfigurationLocationReducer),
    StoreModule.forFeature(CurateSearchKey, CurateSearchReducer),
    StoreModule.forFeature(FittingTreeViewKey, FittingTreeViewReducer),
    StoreModule.forFeature(ElectricalSettingsKey, ElectricalSettingsReducer),
    StoreModule.forFeature(AnnotationSettingsKey, AnnotationSettingsReducer),
    StoreModule.forFeature(SystemManagerKey, SystemManagerReducer),
    StoreModule.forFeature(AddFamilyInformationKey, AddFamilyInformationReducer),
    StoreModule.forFeature(ProjectDataKey, ProjectDataReducer),

    EffectsModule.forFeature([
      ToolbeltEffects,
      ApplyPanelDataEffects,
      ApplyIdEffects,
      ImportScheduleEffects,
      ConfigurationLocationEffects,
      CurateSearchEffects,
      FittingTreeViewEffects,
      ElectricalSettingsEffects,
      AnnotationSettingsEffects,
      SystemManagerEffects,
      FamilyInformationEffects,
      ProjectDataEffects
    ]),
    StoreDevtoolsModule.instrument({ maxAge: 25 , connectInZone: true}),
  ],

    providers: [
        AuthGuard,
        DataInitializerService,
        BackendService,
        PanelDataService,
        HttpClient,
        DotNetRequestMessageService,
        BuildingDataApiService,
        CurateSearchService,
        LocalStorageService,
        SystemStateManager,
        AnnotationSettingsService,
        AnnotationSettingsLabelsManager,
        FittingTreeViewService,
        FamilyInformationService,
        ProjectDataService,
        SysQueStateService,
        { provide: CURATE_SEARCH_FACADE_TOKEN, useClass: CurateSearchFacade },
        { provide: CONFIGURATION_LOCATION_FACADE_TOKEN, useClass: ConfigurationLocationFacade },
        { provide: FITTING_TREE_VIEW_FACADE_TOKEN, useClass: FittingTreeViewFacade },
        { provide: ADD_FITTING_TAB_FACADE_TOKEN, useClass: AddFittingTabFacade },
        { provide: RESULT_CONTAINER_FACADE_TOKEN, useClass: ResultContainerFacade },
        { provide: IMPORT_SCHEDULE_FACADE_TOKEN, useClass: ImportScheduleFacade },
        { provide: APPLY_ID_FACADE_TOKEN, useClass: ApplyIdFacade },
        { provide: PANEL_DATA_FACADE_TOKEN, useClass: PanelDataFacade },
        { provide: SYSTEM_PANEL_FRAME_FACADE_TOKEN, useClass: SystemPanelFrameFacade },
        { provide: TOOLBELT_FACADE_TOKEN, useClass: ToolbeltFacade },
        { provide: APP_SHELL_FACADE_TOKEN, useClass: AppShellFacade },
        { provide: ROOT_PATH_FACADE_TOKEN, useClass: RootPathSelectorFacade },
        { provide: ADD_FITTING_POPUP_FACADE_TOKEN, useClass: AddFittingPopupFacade },
        { provide: SELECT_CATEGORY_FACADE_TOKEN, useClass: SelectCategoryFacade },
        { provide: SELECT_SYSTEM_FACADE_TOKEN, useClass: SelectSystemFacade },
        { provide: ABOUT_DIALOG_FACADE_TOKEN, useClass: AboutDialogFacade },
        { provide: SYSTEM_CATEGORY_OPTIONS_FACADE_TOKEN, useClass: SystemCategoryOptionsFacade },
        { provide: DELETE_SYSTEM_CATEGORY_FACADE_TOKEN, useClass: DeleteSystemCategoryFacade },
        { provide: CATEGORY_SCOPE_FACADE_TOKEN, useClass: CategoryScopeFacade },
        { provide: SYSQUE_TOOLTIP_FACADE_TOKEN, useClass: SysqueTooltipFacade },
        { provide: ADD_FITTING_PANEL_FRAME_FACADE_TOKEN, useClass: AddFittingPanelFrameFacade },
        { provide: MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN, useClass: ManageModePanelFrameFacade },
        { provide: ELECTRICAL_SETTINGS_FACADE_TOKEN, useClass: ElectricalSettingsFacade },
        { provide: ANNOTATION_SETTINGS_FACADE_TOKEN, useClass: AnnotationSettingsFacade},
        { provide: ADD_FAMILY_INFORMATION_FACADE_TOKEN, useClass: AddFamilyInformationFacade},
        { provide: SYSTEM_MANAGER_FACADE_TOKEN, useClass: SystemManagerFacade},
        { provide: ADD_FITTING_MODE_FACADE_TOKEN, useClass: AddFittingModeFacade },
        { provide: CABLE_TRAY_SYSTEM_FACADE_TOKEN, useClass: CableTraySystemFacade },
        { provide: ADD_FAMILY_INFORMATION_FACADE_TOKEN, useClass: AddFamilyInformationFacade },
        { provide: PROJECT_DATA_FACADE_TOKEN, useClass: ProjectDataFacade }, 
        { provide: IMPORT_SYSTEMS_FACADE_TOKEN, useClass: ImportSystemsFacade },
        { provide: FITTING_PRIORITIZATION_FACADE_TOKEN, useClass: FittingPrioritizationFacade },
        { provide: CONVERT_OPTIONS_FACADE_TOKEN, useClass: ConvertOptionsFacade},
        { provide: SYSQUE_GAUGE_FACADE_TOKEN, useClass: SysQueGaugeFacade}
      ],
    bootstrap: [AppComponent], 
    exports: [RouterModule]   
})
export class AppModule {}